import React, { useState, useRef, useEffect } from "react";
import {
  PenLine,
  CheckCircle2,
  Trash2,
  DollarSign,
  Loader2,
  X,
  Link2,
  CreditCard,
  Briefcase,
  PackageCheck,
  CalendarClock,
  Mail,
  Crown,
} from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { estimatesApi, invoicesApi, documentsApi, paymentProvidersApi } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import SignaturePad from "./SignaturePad";
import ProcessPaymentModal from "./ProcessPaymentModal";

const money = (n) =>
  `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmt = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const inputCls =
  "w-full h-9 px-2.5 rounded-md border border-slate-200 bg-white text-[13px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400";

export default function EstimateSignDeposit({ estimate, onUpdated, onPaid }) {
  const { user } = useAuth();
  // Technicians may sign/deposit estimates linked to their own job (backend enforces ownership).
  const canManage =
    user?.role === "admin" || user?.role === "dispatcher" || user?.role === "technician";
  const total = Number(estimate.total || 0);
  const depositPaid = Number(estimate.depositPaid || 0);
  const depositDue = Math.max(0, total - depositPaid);
  const isConverted = estimate.status === "Converted";

  // Signature capture lives only on the "Present to customer" page now — the
  // editor keeps just the deposit ledger to avoid a duplicate signature flow.
  return (
    <div className="space-y-4">
      <DepositBlock
        estimate={estimate}
        onUpdated={onUpdated}
        onPaid={onPaid}
        canManage={canManage && !isConverted}
        total={total}
        depositPaid={depositPaid}
        depositDue={depositDue}
      />
    </div>
  );
}

// ---------------- Post-signature: create a job ----------------
function CreateJobPopup({ estimate, onClose, onCreated }) {
  const [busy, setBusy] = useState("");

  const create = async (target) => {
    setBusy(target);
    try {
      const job = await estimatesApi.createJobFromEstimate(estimate.id, target);
      toast.success(`Job ${job.id} created · ${target}`);
      onCreated(job);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create job");
      setBusy("");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-6"
      data-testid="create-job-popup"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-slate-200">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Briefcase className="h-4 w-4 text-blue-600" />
            <div className="text-[14px] font-semibold text-slate-900">Create a job · {estimate.id}</div>
          </div>
          <button
            onClick={onClose}
            data-testid="create-job-close"
            className="h-7 w-7 rounded hover:bg-slate-100 flex items-center justify-center"
          >
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-[13px] text-slate-600">
            <strong>{estimate.customer}</strong> accepted this estimate. Turn it into a job now?
          </div>

          <button
            onClick={() => create("In Progress")}
            disabled={!!busy}
            data-testid="create-job-now"
            className="w-full flex items-center gap-3 p-3.5 rounded-xl border-2 border-emerald-200 bg-emerald-50 hover:border-emerald-400 hover:bg-emerald-100 transition-colors text-left disabled:opacity-60"
          >
            <div className="h-10 w-10 rounded-lg bg-emerald-600 flex items-center justify-center shrink-0">
              {busy === "In Progress" ? (
                <Loader2 className="h-5 w-5 text-white animate-spin" />
              ) : (
                <PackageCheck className="h-5 w-5 text-white" />
              )}
            </div>
            <div className="min-w-0">
              <div className="text-[13.5px] font-bold text-emerald-900">Do you have the materials to do the job now?</div>
              <div className="text-[11.5px] text-emerald-700">Creates a job in “In Progress”.</div>
            </div>
          </button>

          <button
            onClick={() => create("Unassigned")}
            disabled={!!busy}
            data-testid="create-job-later"
            className="w-full flex items-center gap-3 p-3.5 rounded-xl border-2 border-blue-200 bg-blue-50 hover:border-blue-400 hover:bg-blue-100 transition-colors text-left disabled:opacity-60"
          >
            <div className="h-10 w-10 rounded-lg bg-blue-600 flex items-center justify-center shrink-0">
              {busy === "Unassigned" ? (
                <Loader2 className="h-5 w-5 text-white animate-spin" />
              ) : (
                <CalendarClock className="h-5 w-5 text-white" />
              )}
            </div>
            <div className="min-w-0">
              <div className="text-[13.5px] font-bold text-blue-900">Does this job need to be scheduled for later?</div>
              <div className="text-[11.5px] text-blue-700">Creates a job in “Unassigned”.</div>
            </div>
          </button>

          <button
            onClick={onClose}
            data-testid="create-job-skip"
            className="w-full text-center text-[12.5px] text-slate-500 hover:text-slate-700 font-medium py-1"
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------- Signature ----------------
function SignatureBlock({ estimate, onUpdated, canManage, onSigned }) {
  const [open, setOpen] = useState(false);
  const sig = estimate.signature;

  const clear = async () => {
    if (!window.confirm("Remove the captured signature?")) return;
    try {
      const updated = await estimatesApi.clearSignature(estimate.id);
      onUpdated(updated);
      toast.success("Signature removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove signature");
    }
  };

  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">
        Customer Signature
      </div>
      <div className="rounded-md border border-slate-200 bg-white p-3">
        {sig ? (
          <div data-testid="signature-display">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-2 flex items-center justify-center">
              <img src={sig.dataUrl} alt="signature" className="max-h-[90px] object-contain" />
            </div>
            <div className="mt-2 flex items-center justify-between">
              <div className="flex items-center gap-1.5 text-[12.5px] text-emerald-700">
                <CheckCircle2 className="h-4 w-4" />
                <span className="font-semibold">{sig.signedBy}</span>
                <span className="text-slate-400">· {fmt(sig.signedAt)}</span>
              </div>
              {canManage && (
                <button
                  onClick={clear}
                  data-testid="signature-remove"
                  className="text-[11.5px] text-slate-400 hover:text-rose-600 font-medium"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div className="text-[12.5px] text-slate-500">
              Not signed yet. Capturing a signature marks this estimate <strong>Accepted</strong>.
            </div>
            {canManage && (
              <Button
                onClick={() => setOpen(true)}
                data-testid="capture-signature-btn"
                className="h-9 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[12.5px] font-semibold shrink-0"
              >
                <PenLine className="h-4 w-4 mr-1.5" /> Capture signature
              </Button>
            )}
          </div>
        )}
      </div>
      {open && (
        <SignatureModal
          estimate={estimate}
          onClose={() => setOpen(false)}
          onSigned={(updated) => {
            setOpen(false);
            onSigned(updated);
            toast.success("Signed — estimate accepted");
          }}
        />
      )}
    </div>
  );
}

function SignatureModal({ estimate, onClose, onSigned }) {
  const padRef = useRef(null);
  const [name, setName] = useState(estimate.customer || "");
  const [hasInk, setHasInk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [signoffText, setSignoffText] = useState("");
  useEffect(() => {
    let cancelled = false;
    paymentProvidersApi
      .getSignoffText()
      .then((d) => { if (!cancelled) setSignoffText(d?.estimateSignoffText || ""); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const save = async () => {
    if (padRef.current?.isEmpty()) {
      toast.error("Please sign before saving");
      return;
    }
    if (!name.trim()) {
      toast.error("Enter the signer's name");
      return;
    }
    setBusy(true);
    try {
      const dataUrl = padRef.current.toDataURL();
      const updated = await estimatesApi.sign(estimate.id, { dataUrl, signedBy: name.trim() });
      onSigned(updated);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save signature");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-6">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg border border-slate-200" data-testid="signature-modal">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <PenLine className="h-4 w-4 text-blue-600" />
            <div className="text-[14px] font-semibold text-slate-900">Capture signature · {estimate.id}</div>
          </div>
          <button onClick={onClose} data-testid="signature-modal-close" className="h-7 w-7 rounded hover:bg-slate-100 flex items-center justify-center">
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          {signoffText && (
            <div
              data-testid="signature-modal-signoff"
              className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-[12.5px] leading-relaxed text-slate-700 whitespace-pre-wrap"
              style={{ maxHeight: "8rem", overflowY: "auto" }}
            >
              {signoffText}
            </div>
          )}
          <div>
            <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">Signed by</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="signature-name"
              placeholder="Customer full name"
              className={inputCls}
            />
          </div>
          <SignaturePad ref={padRef} onChange={setHasInk} />
          <div className="text-[11px] text-slate-500">
            By signing, the customer accepts the estimate total of <strong>{money(estimate.total)}</strong>. This stamps the
            time and moves the estimate to <strong>Accepted</strong>.
          </div>
          <div className="flex items-center justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onClose} className="h-9 text-[13px]">Cancel</Button>
            <Button
              onClick={save}
              disabled={busy || !hasInk}
              data-testid="signature-save"
              className="h-9 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-500 text-white text-[13px] font-semibold"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-1.5" /> Sign & accept</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------- Deposits ----------------
function DepositBlock({ estimate, onUpdated, onPaid, canManage, total, depositPaid, depositDue }) {
  const [busy, setBusy] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const deposits = estimate.deposits || [];

  const reverse = async (depId) => {
    if (!window.confirm("Reverse this deposit?")) return;
    setBusy(true);
    try {
      const updated = await estimatesApi.removeDeposit(estimate.id, depId);
      onUpdated(updated);
      toast.success("Deposit reversed");
    } catch (err) {
      toast.error("Reverse failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Payments</div>
        <div className="text-[11.5px] text-slate-500">
          <span className="font-semibold text-emerald-700">{money(depositPaid)}</span> of {money(total)} ·{" "}
          {money(depositDue)} remaining
        </div>
      </div>
      <div className="rounded-md border border-slate-200 bg-white p-3 space-y-2">
        {deposits.length === 0 ? (
          <div className="text-[12px] text-slate-400 italic">No payments collected yet.</div>
        ) : (
          <div className="space-y-1">
            {deposits.map((d) => (
              <div key={d.id} data-testid={`deposit-row-${d.id}`} className="flex items-center justify-between text-[12.5px] py-1 px-2 rounded bg-slate-50/60">
                <div className="flex items-center gap-2 min-w-0">
                  <DollarSign className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                  <span className="font-semibold text-slate-800">{money(d.amount)}</span>
                  <span className="text-slate-500 uppercase text-[10.5px] font-semibold">{d.method}</span>
                  {d.reference && <span className="text-slate-400 text-[11px]">· {d.reference}</span>}
                  <span className="text-slate-400 text-[11px]">· {fmt(d.recordedAt)}</span>
                </div>
                {canManage && (
                  <button onClick={() => reverse(d.id)} disabled={busy} data-testid={`deposit-remove-${d.id}`} className="h-6 w-6 rounded hover:bg-rose-50 flex items-center justify-center text-slate-400 hover:text-rose-600 disabled:opacity-50">
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {canManage && depositDue > 0 && (
          <div className="pt-2 border-t border-slate-100 flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setPayOpen(true)}
              disabled={busy}
              data-testid="deposit-process-payment"
              className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-[12.5px] font-semibold disabled:opacity-60"
            >
              <DollarSign className="h-3.5 w-3.5" /> Process Payment
            </button>
            <button
              onClick={() => setLinkOpen(true)}
              data-testid="deposit-link-btn"
              className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded-md border border-emerald-200 bg-emerald-50 text-[11.5px] font-semibold text-emerald-700 hover:bg-emerald-100"
            >
              <Link2 className="h-3.5 w-3.5" /> Online deposit link
            </button>
          </div>
        )}
      </div>

      {payOpen && (
        <ProcessPaymentModal
          kind="estimate"
          docId={estimate.id}
          docLabel={`Estimate ${estimate.id}`}
          customerName={estimate.customer}
          balance={depositDue}
          onClose={() => setPayOpen(false)}
          onDone={(updated) => {
            setPayOpen(false);
            onUpdated(updated);
            onPaid?.(estimate);
          }}
        />
      )}

      {linkOpen && (
        <DepositLinkModal estimate={estimate} total={total} depositDue={depositDue} onClose={() => setLinkOpen(false)} />
      )}
    </div>
  );
}

function DepositLinkModal({ estimate, total, depositDue, onClose }) {
  const [amount, setAmount] = useState(String(Math.min(depositDue, Math.round(total * 0.25 * 100) / 100)));
  const amt = parseFloat(amount) || 0;
  const valid = amt >= 0.5 && amt <= depositDue;
  const link = `${window.location.origin}/pay/deposit/${estimate.id}?amount=${amt}`;

  const copy = async () => {
    if (!valid) {
      toast.error(`Amount must be between $0.50 and ${money(depositDue)}`);
      return;
    }
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Deposit link copied — share it with the customer");
      onClose();
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-6">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-slate-200" data-testid="deposit-link-modal">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-emerald-50 to-cyan-50">
          <div className="flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-emerald-600" />
            <div className="text-[14px] font-semibold text-slate-900">Online deposit link · {estimate.id}</div>
          </div>
          <button onClick={onClose} data-testid="deposit-link-close" className="h-7 w-7 rounded hover:bg-slate-100 flex items-center justify-center">
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>
        <div className="p-5 space-y-3.5">
          <div>
            <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">Deposit amount (max {money(depositDue)})</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[14px]">$</span>
              <input
                type="number"
                min={0.5}
                max={depositDue}
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                data-testid="deposit-link-amount"
                className="w-full h-11 pl-7 pr-3 rounded-lg border border-slate-200 text-[15px] font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400"
              />
            </div>
            <div className="mt-2 flex items-center gap-1.5">
              {[0.25, 0.5].map((p) => (
                <button
                  key={p}
                  onClick={() => setAmount(String(Math.min(depositDue, Math.round(total * p * 100) / 100)))}
                  className="px-2 py-1 rounded-md border border-slate-200 text-[11px] font-medium text-slate-600 hover:border-emerald-300 hover:text-emerald-700"
                >
                  {p * 100}% ({money(total * p)})
                </button>
              ))}
            </div>
          </div>
          <div className="p-2.5 rounded-lg border border-slate-200 bg-slate-50 break-all text-[11.5px] font-mono text-slate-600">
            {link}
          </div>
          <div className="text-[11px] text-slate-500">
            The customer pays this deposit by card on a secure Stripe page. It's recorded on the estimate automatically and
            carries over to the invoice on conversion.
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button variant="outline" onClick={onClose} className="h-9 text-[13px]">Cancel</Button>
            <Button onClick={copy} disabled={!valid} data-testid="deposit-link-copy" className="h-9 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-500 text-white text-[13px] font-semibold">
              <Link2 className="h-4 w-4 mr-1.5" /> Copy deposit link
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ---------------- Present-to-customer modal (review + sign + process payment) ----------------
export function EstimatePresentModal({ estimate, totals, customer, plans = [], onClose, onUpdated, onPaymentOrEmailDone }) {
  const padRef = useRef(null);
  const [hasInk, setHasInk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [emailing, setEmailing] = useState(false);
  // Two-step flow: intent must be captured (and saved server-side) BEFORE
  // either payment button unlocks. The chosen action itself only runs once
  // the payment is confirmed server-side (Stripe webhook) — never here.
  const [jobIntent, setJobIntent] = useState(estimate.jobStartIntent || null);
  const [intentSaving, setIntentSaving] = useState(false);
  // Company-configured "Estimate acceptance" paragraph (Company Settings →
  // Customer Sign-Off Paragraphs). Loaded fresh each time so admin edits show up.
  const [signoffText, setSignoffText] = useState("");
  useEffect(() => {
    let cancelled = false;
    paymentProvidersApi
      .getSignoffText()
      .then((d) => { if (!cancelled) setSignoffText(d?.estimateSignoffText || ""); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const sig = estimate.signature;
  const lineItems = estimate.lineItems || [];
  const total = Number(totals?.total ?? estimate.total ?? 0);
  const subtotal = Number(
    totals?.subtotal ??
      lineItems.reduce((s, li) => s + (Number(li.qty) || 0) * (Number(li.unitPrice) || 0), 0)
  );
  const discount = Number(totals?.discount ?? estimate.discount ?? 0);
  const savings = Number(totals?.savings ?? 0);
  const [payInvoice, setPayInvoice] = useState(null); // { id, balanceDue, customer } — driver for ProcessPaymentModal

  // Signature is "captured" once ink is on the pad (about to be saved) or
  // already saved — Step 1 (job timing) appears from that point on, above
  // the payment buttons, which stay disabled until an intent is confirmed.
  const signatureCaptured = !!sig || hasInk;

  const selectIntent = async (value) => {
    if (intentSaving || jobIntent === value) return;
    setIntentSaving(true);
    try {
      const updated = await estimatesApi.patch(estimate.id, { jobStartIntent: value });
      setJobIntent(value);
      onUpdated?.(updated);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save your selection");
    } finally {
      setIntentSaving(false);
    }
  };

  // Ensure the estimate is signed. Does NOT create an invoice — the invoice is
  // only finalized once a payment actually succeeds (or explicitly, for the
  // "Email Invoice & Payment Link" button, right before sending).
  const ensureSigned = async () => {
    let est = estimate;
    if (!est.signature) {
      if (padRef.current?.isEmpty?.()) {
        toast.error("Please capture the customer's signature");
        return null;
      }
      const dataUrl = padRef.current.toDataURL();
      const signed = await estimatesApi.sign(est.id, {
        dataUrl,
        signedBy: est.customer || "Customer",
      });
      onUpdated?.(signed);
      est = signed;
    }
    return est;
  };

  // Member-price preview — applied tier if any, else highest tier (upsell). Fee lines excluded.
  const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
  const appliedPlan = plans.find((p) => p.id === estimate.membershipPlanId) || null;
  const previewPlan =
    appliedPlan ||
    plans.reduce((best, p) => (Number(p.discountPct) > Number(best?.discountPct || 0) ? p : best), null);
  const previewPct = Number(previewPlan?.discountPct) || 0;
  const memberApplied = !!appliedPlan;
  // When a membership is already applied, stored unit prices ARE the member prices,
  // so gross them up to show the list price. Otherwise the unit prices are list.
  const listUnit = (li) =>
    memberApplied && previewPct < 100
      ? round2((Number(li.unitPrice) || 0) / (1 - previewPct / 100))
      : round2(Number(li.unitPrice) || 0);
  const memberUnit = (li) =>
    memberApplied
      ? round2(Number(li.unitPrice) || 0)
      : round2((Number(li.unitPrice) || 0) * (1 - previewPct / 100));
  const eligibleUnitSum = lineItems.reduce(
    (s, li) => (li.isMembershipFee ? s : s + (Number(li.qty) || 0) * (Number(li.unitPrice) || 0)),
    0
  );
  const previewSavings = memberApplied
    ? (savings > 0 ? savings : round2(eligibleUnitSum / (1 - previewPct / 100) - eligibleUnitSum))
    : round2((eligibleUnitSum * previewPct) / 100);
  const taxRate = Number(estimate.taxRate || 0);
  const taxAmt = Number(totals?.tax ?? Math.max(0, Math.round((total - (subtotal - discount)) * 100) / 100));
  const depositPaid = Number(estimate.depositPaid || 0);
  const remainingDue = Math.max(0, total - depositPaid);

  const processPayment = async () => {
    setBusy(true);
    try {
      const est = await ensureSigned();
      if (!est) return;
      // Reuse an existing invoice if one already exists (e.g. created by the
      // "Email Invoice & Payment Link" button) — never create a duplicate.
      if (est.convertedInvoiceId) {
        let inv = null;
        try {
          inv = await invoicesApi.get(est.convertedInvoiceId);
        } catch (_) {
          /* keep going with the fallback balance */
        }
        const balance = Number(inv?.balanceDue ?? remainingDue);
        if (balance <= 0) {
          toast.success("Invoice already paid in full");
          onClose();
          return;
        }
        setPayInvoice({ id: est.convertedInvoiceId, isEstimate: false, balance });
        return;
      }
      // No invoice yet — pay directly against the estimate. The backend only
      // finalizes (converts to) an invoice once the payment actually succeeds,
      // so canceling/failing out of checkout leaves the estimate signed and
      // this button clickable again.
      const due = Math.max(0, Number(est.total || 0) - Number(est.depositPaid || 0));
      if (due <= 0) {
        toast.success("Estimate accepted — no balance due");
        onClose();
        return;
      }
      setPayInvoice({ id: null, isEstimate: true, balance: due });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not complete");
    } finally {
      setBusy(false);
    }
  };

  const emailEstimate = async () => {
    const to = customer?.email;
    if (!to) {
      toast.error("No email address on file for this customer");
      return;
    }
    setEmailing(true);
    try {
      const origin = window.location.origin;
      const est = await ensureSigned();
      if (!est) return;
      // Reuse the linked invoice if one already exists — never create a
      // duplicate. Otherwise finalize it now: this button needs a real,
      // payable invoice to email (unlike Process Payment, which only
      // finalizes on confirmed payment success).
      let invoiceId = est.convertedInvoiceId;
      if (!invoiceId) {
        try {
          const inv = await estimatesApi.convertToInvoice(est.id);
          invoiceId = inv.id;
          onUpdated?.({ ...est, convertedInvoiceId: inv.id, status: "Converted" });
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Could not create the invoice to email");
          return;
        }
      }
      const res = await documentsApi.emailInvoice(invoiceId, {
        to,
        pay_link: `${origin}/pay/${invoiceId}`,
      });
      if (res?.sent) toast.success(`Invoice & payment link emailed to ${to}`);
      else toast.message("Email queued — delivery pending");
      // Payment may happen later — show the Start/Schedule prompt right away.
      onPaymentOrEmailDone?.(est);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send email");
    } finally {
      setEmailing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm sm:p-4" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="estimate-present-modal"
        className="bg-white rounded-none sm:rounded-3xl shadow-2xl w-full max-w-none sm:max-w-[480px] h-full sm:h-auto max-h-screen sm:max-h-[92vh] flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="px-5 py-4 flex items-center justify-between shrink-0">
          <div className="text-[22px] font-black text-[#0B1E3F]">
            Estimate <span className="text-slate-300">#{estimate.id}</span>
          </div>
          <button onClick={onClose} data-testid="present-close" className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center">
            <X className="h-5 w-5 text-slate-300" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 pb-4 space-y-4">
          {/* Line items */}
          <div className="bg-white rounded-2xl p-4">
            <div className="text-[12px] font-bold uppercase tracking-[0.12em] text-slate-400 mb-2">Line-items</div>
            <div className="divide-y divide-slate-700/40">
              {lineItems.map((li, idx) => {
                const showPreview = !li.isMembershipFee && previewPct > 0;
                return (
                <div key={li.id || idx} className="flex items-start justify-between gap-3 py-3" data-testid="present-line-item">
                  <div className="min-w-0">
                    <div className="text-[15px] text-[#0B1E3F] truncate">{li.description}</div>
                    {showPreview && (
                      <div className="text-[12px] mt-1 flex items-center gap-1.5" data-testid="present-line-member-price">
                        <span className="text-slate-400 line-through">{money(listUnit(li)).replace(/\.00$/, "")}</span>
                        <span className="text-slate-400">→</span>
                        <span className="text-violet-300 font-bold">{money(memberUnit(li)).replace(/\.00$/, "")}</span>
                        <span className="text-violet-300/80">member</span>
                      </div>
                    )}
                  </div>
                  <span className="text-[15px] text-slate-200 shrink-0">
                    {Number(li.qty) || 0} × {money(li.unitPrice).replace(/\.00$/, "")}
                  </span>
                </div>
                );
              })}
            </div>
            <div className="pt-3 mt-1 border-t border-slate-700/40 space-y-1 text-right">
              <div className="text-[14px] text-slate-300">Subtotal: <span className="text-slate-200 font-semibold">{money(subtotal).replace(/\.00$/, "")}</span></div>
              {savings > 0 && (
                <div className="text-[14px] text-slate-300">Membership savings: <span className="text-violet-300 font-semibold">−{money(savings).replace(/\.00$/, "")}</span></div>
              )}
              {discount > 0 && (
                <div className="text-[14px] text-slate-300">Discount: <span className="text-slate-200 font-semibold">−{money(discount).replace(/\.00$/, "")}</span></div>
              )}
              <div className="text-[14px] text-slate-300">Tax ({taxRate}%): <span className="text-slate-200 font-semibold">{money(taxAmt).replace(/\.00$/, "")}</span></div>
              <div className="text-[20px] font-black text-[#0B1E3F] pt-1">Total: {money(total).replace(/\.00$/, "")}</div>
            </div>
            {previewPct > 0 && previewSavings > 0 && (
              <div
                data-testid="present-member-upsell"
                className="mt-3 rounded-xl border border-violet-400/40 bg-violet-500/10 px-3 py-2.5 flex items-center gap-2.5"
              >
                <Crown className="h-4 w-4 text-violet-300 shrink-0" />
                <div className="text-[13px] text-slate-200 leading-snug text-left">
                  {memberApplied ? (
                    <>As a <span className="font-bold text-violet-200">{previewPlan?.name}</span> member, you're saving <span className="font-bold text-violet-200">{money(previewSavings).replace(/\.00$/, "")}</span> on this estimate.</>
                  ) : (
                    <>Become a <span className="font-bold text-violet-200">{previewPlan?.name}</span> member and save <span className="font-bold text-violet-200">{money(previewSavings).replace(/\.00$/, "")}</span> on this estimate — {previewPct}% off every visit.</>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Signature */}
          <div className="bg-white rounded-2xl p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <PenLine className="h-4 w-4 text-blue-400" />
                <span className="text-[12px] font-bold uppercase tracking-[0.12em] text-slate-400">Customer Signature</span>
              </div>
              <button
                onClick={() => padRef.current?.clear?.()}
                data-testid="present-sig-clear"
                className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-blue-400"
              >
                <PenLine className="h-3.5 w-3.5" /> Clear
              </button>
            </div>
            {sig ? (
              <div className="rounded-2xl border border-blue-500/30 p-3 flex items-center justify-center" style={{ background: "#ffffff" }} data-testid="present-sig-existing">
                <img src={sig.dataUrl} alt="signature" className="max-h-[120px] object-contain" />
              </div>
            ) : (
              <>
                {signoffText && (
                  <div
                    data-testid="present-signoff-paragraph"
                    className="mb-3 rounded-xl bg-white border border-slate-200 px-3 py-2.5 text-[12.5px] leading-relaxed text-black whitespace-pre-wrap"
                  >
                    {signoffText}
                  </div>
                )}
                <div className="rounded-2xl border border-blue-500/30 overflow-hidden" data-testid="present-sig-pad">
                  <SignaturePad ref={padRef} onChange={setHasInk} height={150} />
                </div>
              </>
            )}
          </div>

          {/* Step 1 — job timing intent (must be chosen before payment unlocks) */}
          {signatureCaptured && (
            <div className="bg-white rounded-2xl p-4" data-testid="job-intent-step">
              <div className="flex items-center gap-2 mb-3">
                <Briefcase className="h-4 w-4 text-blue-400" />
                <span className="text-[12px] font-bold uppercase tracking-[0.12em] text-slate-400">Step 1 · Job timing</span>
              </div>
              <div className="grid grid-cols-1 gap-2.5">
                <button
                  type="button"
                  onClick={() => selectIntent("now")}
                  disabled={intentSaving}
                  data-testid="job-intent-now"
                  className={`w-full h-12 rounded-xl border-2 text-[13.5px] font-bold transition-colors flex items-center justify-center gap-2 disabled:opacity-60 ${
                    jobIntent === "now"
                      ? "border-emerald-500 bg-emerald-50 text-emerald-800"
                      : "border-slate-200 text-slate-700 hover:border-emerald-300"
                  }`}
                >
                  {intentSaving && jobIntent !== "now" ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <PackageCheck className="h-4 w-4" />
                  )}
                  Yes, I can start the job now
                </button>
                <button
                  type="button"
                  onClick={() => selectIntent("schedule")}
                  disabled={intentSaving}
                  data-testid="job-intent-schedule"
                  className={`w-full h-12 rounded-xl border-2 text-[13.5px] font-bold transition-colors flex items-center justify-center gap-2 disabled:opacity-60 ${
                    jobIntent === "schedule"
                      ? "border-blue-500 bg-blue-50 text-blue-800"
                      : "border-slate-200 text-slate-700 hover:border-blue-300"
                  }`}
                >
                  {intentSaving && jobIntent !== "schedule" ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <CalendarClock className="h-4 w-4" />
                  )}
                  No, this job needs to be scheduled
                </button>
              </div>
              {!jobIntent && (
                <p className="mt-2.5 text-[11.5px] text-slate-400" data-testid="job-intent-hint">
                  Pick one to unlock the payment options below.
                </p>
              )}
            </div>
          )}

        </div>

        {/* Process Payment CTA */}
        <div className="px-5 py-4 shrink-0 border-t border-slate-700/40">
          <button
            onClick={processPayment}
            disabled={busy || !jobIntent}
            data-testid="present-process-payment"
            className="w-full h-14 rounded-2xl bg-[#2563EB] text-white text-[17px] font-black active:scale-[0.98] transition-transform disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
            Process Payment
          </button>
          <button
            onClick={emailEstimate}
            disabled={emailing || !jobIntent}
            data-testid="present-email-estimate"
            className="w-full mt-3 h-12 rounded-2xl border border-blue-500/50 text-blue-300 text-[15px] font-bold active:bg-blue-500/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {emailing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
            Email Invoice &amp; Payment Link
          </button>
        </div>

        {payInvoice && (
          <ProcessPaymentModal
            kind={payInvoice.isEstimate ? "estimate" : "invoice"}
            docId={payInvoice.isEstimate ? estimate.id : payInvoice.id}
            docLabel={payInvoice.isEstimate ? `Estimate ${estimate.id}` : `Invoice ${payInvoice.id}`}
            customerName={estimate.customer}
            balance={payInvoice.balance}
            onClose={() => setPayInvoice(null)}
            onDone={(updated) => {
              setPayInvoice(null);
              onUpdated?.(updated);
              if (onPaymentOrEmailDone) onPaymentOrEmailDone(estimate);
              else onClose();
            }}
          />
        )}
      </div>
    </div>
  );
}
